// src/pages/ArgotPractice.jsx
import React, { useState, useMemo, useEffect } from 'react';

// Milestone 1: Flashcards (Unchanged)
const FlashcardView = ({ terms }) => {
  const [index, setIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const term = terms[index];

  const nextCard = () => {
    setIsFlipped(false);
    setIndex((prev) => (prev + 1) % terms.length);
  };

  const prevCard = () => {
    setIsFlipped(false);
    setIndex((prev) => (prev - 1 + terms.length) % terms.length);
  };

  return (
    <div className="py-10">
      <h3 className="text-2xl font-semibold">🃏 Milestone 1: Flashcards</h3>
      <div 
        className="w-full h-64 flex items-center justify-center p-6 bg-gray-100 rounded-lg shadow-inner cursor-pointer"
        onClick={() => setIsFlipped(!isFlipped)}
      >
        <p className="text-3xl font-bold text-center">{isFlipped ? term.definition : term.term}</p>
      </div>
      <p className="text-center text-gray-500 mt-2">{isFlipped ? "Definition" : "Term"} (Click card to flip)</p>
      <div className="flex justify-between items-center mt-6">
        <button onClick={prevCard} className="px-4 py-2 bg-gray-200 rounded">Prev</button>
        <p>{index + 1} / {terms.length}</p>
        <button onClick={nextCard} className="px-4 py-2 bg-indigo-600 text-white rounded">Next</button>
      </div>
    </div>
  );
};

// Milestone 2: Multiple Choice Test (Unchanged)
const TestView = ({ terms, onPassTest }) => {
  const quizTerms = useMemo(() => terms.slice(0, 10), [terms]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const currentQuestion = quizTerms[currentQuestionIndex];

  const options = useMemo(() => {
    if (!currentQuestion) return [];
    const correctAnswer = currentQuestion.definition;
    const wrongAnswers = terms.filter(term => term.id !== currentQuestion.id).sort(() => 0.5 - Math.random()).slice(0, 3).map(term => term.definition);
    return [correctAnswer, ...wrongAnswers].sort(() => 0.5 - Math.random());
  }, [currentQuestion, terms]);

  useEffect(() => {
    if (showResults) {
      const percentage = (score / quizTerms.length) * 100;
      if (percentage >= 80) { onPassTest(); }
    }
  }, [showResults, score, quizTerms.length, onPassTest]);

  const handleAnswerClick = (answer) => {
    if (isAnswered) return;
    setIsAnswered(true);
    setSelectedAnswer(answer);
    if (answer === currentQuestion.definition) { setScore(prevScore => prevScore + 1); }
  };

  const handleNext = () => {
    const nextIndex = currentQuestionIndex + 1;
    if (nextIndex < quizTerms.length) {
      setCurrentQuestionIndex(nextIndex);
      setIsAnswered(false);
      setSelectedAnswer(null);
    } else {
      setShowResults(true);
    }
  };

  const handleRestart = () => {
    setCurrentQuestionIndex(0);
    setScore(0);
    setSelectedAnswer(null);
    setIsAnswered(false);
    setShowResults(false);
  };

  const getButtonClass = (option) => {
    if (!isAnswered) return 'bg-gray-100 hover:bg-gray-200';
    if (option === currentQuestion.definition) return 'bg-green-500 text-white';
    if (option === selectedAnswer) return 'bg-red-500 text-white';
    return 'bg-gray-100 disabled:opacity-50';
  };

  if (showResults) {
    const percentage = (score / quizTerms.length) * 100;
    const passed = percentage >= 80;
    return (
      <div className="py-10 text-center">
        <h3 className="text-2xl font-semibold">✏️ Quiz Results</h3>
        <p className="text-4xl font-bold my-4">{score} / {quizTerms.length}</p>
        <p className={`text-xl font-semibold ${passed ? 'text-green-600' : 'text-red-600'}`}>
          {passed ? 'Congratulations, you passed! Milestone 3 is unlocked.' : 'You need 80% to pass. Keep trying!'}
        </p>
        <button onClick={handleRestart} className="mt-8 px-6 py-2 bg-indigo-600 text-white font-semibold rounded-lg shadow">Restart Test</button>
      </div>
    );
  }
  
  if (!currentQuestion) return <p>Loading test...</p>;

  return (
    <div className="py-10">
      <h3 className="text-2xl font-semibold">✏️ Milestone 2: Multiple Choice</h3>
      <p className="text-gray-500 mb-2">Question {currentQuestionIndex + 1} of {quizTerms.length}</p>
      <div className="w-full p-6 bg-indigo-600 text-white rounded-lg shadow-lg my-4">
        <p className="text-sm uppercase">Term:</p>
        <p className="text-3xl font-bold text-center">{currentQuestion.term}</p>
      </div>
      <p className="font-semibold mb-2">Which is the correct definition?</p>
      <div className="space-y-3">
        {options.map((option, index) => (
          <button
            key={index}
            onClick={() => handleAnswerClick(option)}
            disabled={isAnswered}
            className={`w-full p-4 text-left rounded-lg transition-colors ${getButtonClass(option)}`}
          >{option}</button>
        ))}
      </div>
      {isAnswered && (
        <button onClick={handleNext} className="mt-6 w-full px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg shadow">
          {currentQuestionIndex === quizTerms.length - 1 ? 'Show Results' : 'Next Question'}
        </button>
      )}
    </div>
  );
};
// --- END OF TEST VIEW ---


// --- ✅ NEW: Milestone 3: Functional Term Recall ---
const RecallView = ({ terms }) => {

  // ✅ FIX 1: Robust filtering
  // This new filter is case-insensitive and trims whitespace.
  const recallableTerms = useMemo(() => {
    return terms.filter(t => {
      if (!t.term || !t.example) return false; // Ensure data exists
      
      // Trim whitespace and check case-insensitively
      const termLower = t.term.trim().toLowerCase();
      const exampleLower = t.example.toLowerCase();
      
      return exampleLower.includes(termLower);
    });
  }, [terms]);

  const [index, setIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [isAnswered, setIsAnswered] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  if (recallableTerms.length === 0) {
    return (
      <div className="py-10">
        <h3 className="text-2xl font-semibold">📝 Milestone 3: Term Recall</h3>
        <p className="text-gray-500 mt-4">No valid example sentences are available for this course's recall exercise yet.</p>
        <p className="text-sm text-gray-400 mt-2">(This means the 'example' field in your database doesn't contain the 'term'.)</p>
      </div>
    );
  }

  const currentTerm = recallableTerms[index];
  
  // ✅ FIX 2: Robust replace
  // This uses a case-insensitive RegExp to blank out the term.
  const blankedSentence = currentTerm.example.replace(
    new RegExp(currentTerm.term.trim(), 'i'), // 'i' flag for case-insensitive
    '_______'
  );

  const checkAnswer = () => {
    if (isAnswered) return;

    const correct = userAnswer.trim().toLowerCase() === currentTerm.term.trim().toLowerCase();
    setIsCorrect(correct);
    setIsAnswered(true);
  };

  const handleNext = () => {
    setIsAnswered(false);
    setIsCorrect(false);
    setUserAnswer('');
    setIndex((prev) => (prev + 1) % recallableTerms.length);
  };

  return (
    <div className="py-10">
      <h3 className="text-2xl font-semibold">📝 Milestone 3: Term Recall</h3>
      <p className="mt-2 text-gray-600">Fill in the blank with the correct professional term.</p>
      <p className="text-gray-500 mb-6">Question {index + 1} of {recallableTerms.length}</p>

      {/* The Question */}
      <div className="mt-6 p-6 border rounded-lg bg-gray-50">
        <p className="text-xl text-gray-700">{blankedSentence}</p>
      </div>

      {/* The Answer Input */}
      <div className="mt-4">
        <input 
          type="text" 
          value={userAnswer}
          onChange={(e) => setUserAnswer(e.target.value)}
          disabled={isAnswered}
          className={`w-full p-3 border-2 rounded ${isAnswered ? (isCorrect ? 'border-green-500' : 'border-red-500') : 'border-gray-300'}`}
          placeholder="Type the missing term..."
          onKeyDown={(e) => e.key === 'Enter' && checkAnswer()}
        />
      </div>

      {/* Feedback Section */}
      {isAnswered && (
        <div className={`mt-4 p-4 rounded-lg ${isCorrect ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          <p className="font-bold">{isCorrect ? 'Correct!' : 'Not quite.'}</p>
          {!isCorrect && <p>The correct answer was: <strong>{currentTerm.term}</strong></p>}
        </div>
      )}

      {/* Action Buttons */}
      <div className="mt-6">
        {isAnswered ? (
          <button 
            onClick={handleNext}
            className="w-full px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg shadow"
          >
            Next Question
          </button>
        ) : (
          <button 
            onClick={checkAnswer}
            className="w-full px-6 py-3 bg-indigo-600 text-white font-semibold rounded-lg shadow"
          >
            Check Answer
          </button>
        )}
      </div>
    </div>
  );
};
// --- END OF NEW RECALL VIEW ---


// Main component that selects which view to show
const ArgotPractice = ({ terms, mode, onFinish, onPassTest }) => {
  const shuffledTerms = useMemo(() => [...terms].sort(() => 0.5 - Math.random()), [terms]);

  const renderExercise = () => {
    switch (mode) {
      case 'flashcards': 
        return <FlashcardView terms={shuffledTerms} />;
      case 'test': 
        return <TestView terms={shuffledTerms} onPassTest={onPassTest} />;
      case 'recall': 
        return <RecallView terms={shuffledTerms} />;
      default: 
        return <p>Invalid exercise mode.</p>;
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white shadow-lg rounded-xl">
      <button onClick={onFinish} className="mb-6 px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300">
        &larr; Back to Milestones
      </button>
      {renderExercise()}
    </div>
  );
};

export default ArgotPractice;